import type { Schemas } from "@platform/contracts/client";
import {
  Button,
  Card,
  decimalAmount,
  Input,
  Label,
  minorAmount,
  SegmentedTabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@platform/ui";
import { ErrorText, errorKey, issuesOf, issueText, useSession, useText } from "@platform/ui/features";
import { type FormEvent, type ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslations } from "use-intl";
import { useStepUp } from "../components/step-up";
import { formatDateTime } from "../lib/i18n";

type RuleSet = Schemas["RuleSet"];
type Values = Schemas["RuleSetValues"];
type Flag = Schemas["FeatureFlag"];
type Corridor = Schemas["Corridor"];
type Tab = "rules" | "flags" | "corridors";

const PERCENT = /^(\d{1,3})(?:\.(\d{1,2}))?$/;

/** A percentage typed as "2.5" in basis points (250), by string alone; undefined when it is not one. */
export function percentBps(text: string): number | undefined {
  const match = PERCENT.exec(text.trim());
  if (match === null) return undefined;
  const bps = `${match[1]}${(match[2] ?? "").padEnd(2, "0")}`;
  const value = Number.parseInt(bps, 10);
  return value <= 10_000 ? value : undefined;
}

const bpsText = (bps: number) => {
  const whole = Math.trunc(bps / 100);
  const fraction = String(bps % 100)
    .padStart(2, "0")
    .replace(/0+$/, "");
  return fraction === "" ? String(whole) : `${whole}.${fraction}`;
};

interface RulesForm {
  inquiryExpiryDays: string;
  quoteValidityDefault: string;
  quoteValidityMax: string;
  paymentWindowDays: string;
  confirmationWindowDays: string;
  reminderDaysBeforeEnd: string;
  afterSalesWindowDays: string;
  afterSalesResponseDays: string;
  afterSalesEvidenceDays: string;
  afterSalesAppealDays: string;
  feePercent: string;
  gstPercent: string;
  feeMinimum: string;
  feeCap: string;
  notServed: string;
  regionsNotServed: string;
  notOpen: string;
  effectiveFrom: string;
}

/** Codes as an operator types them: separated by spaces or commas, any case. */
const codesOf = (text: string) =>
  text
    .split(/[\s,]+/)
    .map((code) => code.trim().toUpperCase())
    .filter((code) => code !== "");

const formOf = (values: Values): RulesForm => ({
  inquiryExpiryDays: String(values.inquiryExpiryDays),
  quoteValidityDefault: String(values.quoteValidityDays.default),
  quoteValidityMax: String(values.quoteValidityDays.max),
  paymentWindowDays: String(values.paymentWindowDays),
  confirmationWindowDays: String(values.confirmation.windowDays),
  reminderDaysBeforeEnd: String(values.confirmation.reminderDaysBeforeEnd),
  afterSalesWindowDays: String(values.afterSales.windowDays),
  afterSalesResponseDays: String(values.afterSales.responseDays),
  afterSalesEvidenceDays: String(values.afterSales.evidenceDays),
  afterSalesAppealDays: String(values.afterSales.appealDays),
  feePercent: bpsText(values.fee.rateBps),
  gstPercent: bpsText(values.gst.rateBps),
  feeMinimum: decimalAmount(values.fee.minimum),
  feeCap: decimalAmount(values.fee.cap),
  notServed: (values.countries?.notServed ?? []).join(" "),
  regionsNotServed: (values.countries?.regionsNotServed ?? []).join(" "),
  notOpen: (values.countries?.notOpen ?? []).join(" "),
  effectiveFrom: "",
});

/** The form as the API takes it, or the fields that cannot be read. */
export function valuesOf(form: RulesForm, currency: string): { values?: Values; invalid: string[] } {
  const invalid: string[] = [];
  const days = (field: keyof RulesForm) => {
    const value = /^\d{1,3}$/.test(form[field].trim()) ? Number.parseInt(form[field], 10) : Number.NaN;
    if (Number.isNaN(value)) invalid.push(field);
    return value;
  };
  const money = (field: "feeMinimum" | "feeCap") => {
    const amountMinor = minorAmount(form[field], currency);
    if (amountMinor === undefined) invalid.push(field);
    return { amountMinor: amountMinor ?? "0", currency } as Values["fee"]["minimum"];
  };
  const rateBps = percentBps(form.feePercent);
  if (rateBps === undefined) invalid.push("feePercent");
  const gstBps = percentBps(form.gstPercent);
  if (gstBps === undefined) invalid.push("gstPercent");
  // The places served (ADR 0025, card 127): countries by ISO 3166-1, regions by ISO 3166-2.
  const countries = (field: "notServed" | "notOpen") => {
    const codes = codesOf(form[field]);
    if (codes.some((code) => !/^[A-Z]{2}$/.test(code))) invalid.push(field);
    return codes as NonNullable<Values["countries"]>["notServed"];
  };
  const regions = codesOf(form.regionsNotServed);
  if (regions.some((code) => !/^[A-Z]{2}-[A-Z0-9]{1,3}$/.test(code))) invalid.push("regionsNotServed");
  const values: Values = {
    inquiryExpiryDays: days("inquiryExpiryDays"),
    quoteValidityDays: { default: days("quoteValidityDefault"), max: days("quoteValidityMax") },
    paymentWindowDays: days("paymentWindowDays"),
    confirmation: {
      windowDays: days("confirmationWindowDays"),
      reminderDaysBeforeEnd: days("reminderDaysBeforeEnd"),
    },
    afterSales: {
      windowDays: days("afterSalesWindowDays"),
      responseDays: days("afterSalesResponseDays"),
      evidenceDays: days("afterSalesEvidenceDays"),
      appealDays: days("afterSalesAppealDays"),
    },
    gst: { rateBps: gstBps ?? 0 },
    fee: { rateBps: rateBps ?? 0, minimum: money("feeMinimum"), cap: money("feeCap") },
    countries: {
      notServed: countries("notServed"),
      regionsNotServed: regions,
      notOpen: countries("notOpen"),
    },
  };
  return invalid.length === 0 ? { values, invalid } : { invalid };
}

const DAY_FIELDS = [
  "inquiryExpiryDays",
  "quoteValidityDefault",
  "quoteValidityMax",
  "paymentWindowDays",
  "confirmationWindowDays",
  "reminderDaysBeforeEnd",
  "afterSalesWindowDays",
  "afterSalesResponseDays",
  "afterSalesEvidenceDays",
  "afterSalesAppealDays",
] as const;

function RulesTab() {
  const t = useTranslations();
  const text = useText();
  const { session } = useSession();
  const stepUp = useStepUp();
  const [items, setItems] = useState<readonly RuleSet[]>([]);
  const [current, setCurrent] = useState<number | undefined>();
  const [form, setForm] = useState<RulesForm | undefined>();
  const [messages, setMessages] = useState<readonly string[]>([]);
  const [notice, setNotice] = useState<string | undefined>();

  const load = useCallback(async () => {
    const { data, error } = await session.api.GET("/v1/ops/rulesets");
    if (data === undefined) return setMessages([t(errorKey(error))]);
    setItems(data.items);
    setCurrent(data.currentVersion);
    const inForce = data.items.find((item) => item.version === data.currentVersion);
    if (inForce !== undefined) setForm(formOf(inForce.values));
  }, [session, t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (form === undefined) return messages.length > 0 ? <ErrorText>{messages[0]}</ErrorText> : null;
  const inForce = items.find((item) => item.version === current);
  const currency = inForce?.values.fee.minimum.currency ?? "SGD";

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setNotice(undefined);
    const { values, invalid } = valuesOf(form, currency);
    if (values === undefined)
      return setMessages(
        invalid
          .map((field) => t(`admin.settings.rules.fields.${field}`))
          .map((label) => t("admin.settings.invalid", { field: label })),
      );
    setMessages([]);
    const effectiveFrom = form.effectiveFrom === "" ? undefined : new Date(form.effectiveFrom).toISOString();
    const outcome = await stepUp.run(() =>
      session.api.POST("/v1/ops/rulesets", {
        body: { values, ...(effectiveFrom === undefined ? {} : { effectiveFrom }) },
      }),
    );
    if (outcome.error !== undefined) {
      const issues = issuesOf(outcome.error);
      return setMessages(
        issues === undefined
          ? [t(errorKey(outcome.error))]
          : Object.entries(issues).map(([path, code]) => `${path}: ${issueText(text.t, code) ?? code}`),
      );
    }
    setNotice(t("admin.settings.rules.saved"));
    await load();
  };

  const field = (name: keyof RulesForm, props: { inputMode?: "numeric" | "decimal"; type?: string } = {}) => (
    <div key={name} className="flex flex-col gap-1.5">
      <Label htmlFor={`rules-${name}`}>{t(`admin.settings.rules.fields.${name}`)}</Label>
      <Input
        id={`rules-${name}`}
        value={form[name]}
        {...props}
        onChange={(event) => setForm({ ...form, [name]: event.target.value })}
      />
    </div>
  );

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">
      <Card>
        <form className="flex flex-col gap-4" onSubmit={(event) => void save(event)}>
          <h2 className="text-subheading font-semibold text-ink">
            {t("admin.settings.rules.title", { version: current ?? 0 })}
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {DAY_FIELDS.map((name) => field(name, { inputMode: "numeric" }))}
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            {field("feePercent", { inputMode: "decimal" })}
            {field("feeMinimum", { inputMode: "decimal" })}
            {field("feeCap", { inputMode: "decimal" })}
          </div>
          <p className="text-caption text-ink-muted">{t("admin.settings.rules.feeCurrency", { currency })}</p>
          <div className="grid gap-3 sm:grid-cols-3">{field("gstPercent", { inputMode: "decimal" })}</div>
          <p className="text-caption text-ink-muted">{t("admin.settings.rules.gstHint")}</p>
          <div className="grid gap-3 sm:grid-cols-3">
            {field("notServed")}
            {field("regionsNotServed")}
            {field("notOpen")}
          </div>
          <p className="text-caption text-ink-muted">{t("admin.settings.rules.countriesHint")}</p>
          {field("effectiveFrom", { type: "datetime-local" })}
          <p className="text-caption text-ink-muted">{t("admin.settings.rules.effectiveHint")}</p>
          {messages.map((message) => (
            <ErrorText key={message}>{message}</ErrorText>
          ))}
          {notice !== undefined && <p className="text-small text-tone-done-fg">{notice}</p>}
          <Button type="submit" className="self-start">
            {t("admin.settings.rules.save")}
          </Button>
        </form>
      </Card>
      <Card className="flex h-fit flex-col gap-2 text-small">
        <h2 className="text-subheading font-semibold text-ink">{t("admin.settings.rules.history")}</h2>
        <ul className="flex flex-col gap-1">
          {items.map((item) => (
            <li key={item.version} className="flex flex-wrap justify-between gap-2">
              <span>
                {t("admin.settings.rules.version", { version: item.version })}
                {item.version === current && (
                  <span className="ml-2 text-tone-done-fg">{t("admin.settings.rules.inForce")}</span>
                )}
              </span>
              <span className="text-ink-muted">{formatDateTime(item.effectiveFrom)}</span>
            </li>
          ))}
        </ul>
      </Card>
      {stepUp.dialog}
    </div>
  );
}

function FlagsTab() {
  const t = useTranslations();
  const { session } = useSession();
  const stepUp = useStepUp();
  const [flags, setFlags] = useState<readonly Flag[]>([]);
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | undefined>();

  const load = useCallback(async () => {
    const { data, error: problem } = await session.api.GET("/v1/ops/feature-flags");
    if (data === undefined) return setError(t(errorKey(problem)));
    setFlags(data.items);
  }, [session, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const flip = async (flag: Flag) => {
    setError(undefined);
    const outcome = await stepUp.run(() =>
      session.api.PUT("/v1/ops/feature-flags/{key}", {
        params: { path: { key: flag.key } },
        body: { value: !flag.value, reason: (reasons[flag.key] ?? "").trim() },
      }),
    );
    if (outcome.error !== undefined) return setError(t(errorKey(outcome.error)));
    setReasons({ ...reasons, [flag.key]: "" });
    await load();
  };

  return (
    <div className="flex flex-col gap-3">
      {flags.map((flag) => (
        <Card key={flag.key} className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-col">
              <span className="font-medium">{t(`admin.settings.flags.names.${flag.key}`)}</span>
              <span className="text-small text-ink-muted">{t(`admin.settings.flags.about.${flag.key}`)}</span>
            </div>
            <span
              className={
                flag.value ? "text-small font-medium text-tone-done-fg" : "text-small text-ink-muted"
              }
            >
              {t(flag.value ? "admin.settings.flags.on" : "admin.settings.flags.off")}
            </span>
          </div>
          <p className="text-caption text-ink-muted">
            {t("admin.settings.flags.changed", { when: formatDateTime(flag.updatedAt) })}
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex min-w-72 flex-1 flex-col gap-1.5">
              <Label htmlFor={`flag-${flag.key}-reason`}>{t("admin.settings.flags.reason")}</Label>
              <Input
                id={`flag-${flag.key}-reason`}
                value={reasons[flag.key] ?? ""}
                maxLength={500}
                onChange={(event) => setReasons({ ...reasons, [flag.key]: event.target.value })}
              />
            </div>
            <Button
              variant="secondary"
              disabled={(reasons[flag.key] ?? "").trim().length < 3}
              onClick={() => void flip(flag)}
            >
              {t(flag.value ? "admin.settings.flags.turnOff" : "admin.settings.flags.turnOn")}
            </Button>
          </div>
        </Card>
      ))}
      {error !== undefined && <ErrorText>{error}</ErrorText>}
      {stepUp.dialog}
    </div>
  );
}

function CorridorsTab() {
  const t = useTranslations();
  const { session } = useSession();
  const stepUp = useStepUp();
  const [rows, setRows] = useState<readonly Corridor[]>([]);
  const [error, setError] = useState<string | undefined>();

  const load = useCallback(async () => {
    const { data, error: problem } = await session.api.GET("/v1/ops/corridors");
    if (data === undefined) return setError(t(errorKey(problem)));
    setRows(data.items);
  }, [session, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (row: Corridor, changes: Partial<Corridor>) => {
    setError(undefined);
    const next = { ...row, ...changes };
    const outcome = await stepUp.run(() =>
      session.api.PUT("/v1/ops/corridors/{currency}", {
        params: { path: { currency: row.currency } },
        body: {
          enabled: next.enabled,
          paymentMethods: [...next.paymentMethods],
          payoutSupported: next.payoutSupported,
          notes: next.notes,
        },
      }),
    );
    if (outcome.error !== undefined) return setError(t(errorKey(outcome.error)));
    await load();
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-small text-ink-muted">{t("admin.settings.corridors.intro")}</p>
      <div className="overflow-hidden rounded-(--radius-card) border border-border bg-surface">
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>{t("admin.settings.corridors.currency")}</TableHeaderCell>
              <TableHeaderCell>{t("admin.settings.corridors.enabled")}</TableHeaderCell>
              <TableHeaderCell>{t("admin.settings.corridors.payout")}</TableHeaderCell>
              <TableHeaderCell>{t("admin.settings.corridors.changed")}</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.currency}>
                <TableCell className="font-medium">{row.currency}</TableCell>
                <TableCell>
                  <input
                    type="checkbox"
                    aria-label={t("admin.settings.corridors.enable", { currency: row.currency })}
                    checked={row.enabled}
                    onChange={() => void save(row, { enabled: !row.enabled })}
                  />
                </TableCell>
                <TableCell>
                  <input
                    type="checkbox"
                    aria-label={t("admin.settings.corridors.payoutFor", { currency: row.currency })}
                    checked={row.payoutSupported}
                    onChange={() => void save(row, { payoutSupported: !row.payoutSupported })}
                  />
                </TableCell>
                <TableCell className="text-ink-muted">{formatDateTime(row.updatedAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {error !== undefined && <ErrorText>{error}</ErrorText>}
      {stepUp.dialog}
    </div>
  );
}

const PANELS: Record<Tab, () => ReactNode> = { rules: RulesTab, flags: FlagsTab, corridors: CorridorsTab };

/**
 * Trade settings (ADM-4, card 050), for operators with `ops:config.manage`: the RuleSet as versions that start now or
 * later and are never backdated, the feature flags with a reason for each change, and the currencies. Every change
 * asks for a fresh TOTP step-up and is audited by the API.
 */
export function SettingsPage() {
  const t = useTranslations();
  const [tab, setTab] = useState<Tab>("rules");
  const Panel = PANELS[tab];
  return (
    <div className="flex flex-col gap-5">
      <h1 className="text-title font-semibold text-ink">{t("admin.settings.title")}</h1>
      <SegmentedTabs
        label={t("admin.settings.title")}
        value={tab}
        onChange={(next) => setTab(next as typeof tab)}
        items={(["rules", "flags", "corridors"] as const).map((each) => ({
          id: each,
          label: t(`admin.settings.tabs.${each}`),
        }))}
      />
      <Panel />
    </div>
  );
}
